import { PlayButton } from "@vidstack/react";
import { PlayIcon, PauseIcon } from "@vidstack/react/icons";
import {
	SkipBack,
	SkipForward,
	RotateCcw,
	RotateCw,
	Play,
	Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	getPlayerActions,
	playerSelectors,
	usePlayerStore,
} from "@/lib/player/player.store";
import { SKIP_INTERVAL_SECONDS } from "./player.constants";

interface PlayerControlsProps {
	isPaused: boolean;
	/** Style variant for different contexts */
	variant?: "desktop" | "mobile";
	/** Custom play/pause handler (for mobile without Vidstack button) */
	onPlayPause?: () => void;
}

/**
 * PlayerControls Component
 *
 * Main playback controls for the media player:
 * - Previous chapter button
 * - 30 second rewind button
 * - Play/Pause button
 * - 30 second forward button
 * - Next chapter button
 *
 * Supports both desktop (compact) and mobile (large touch-friendly) variants
 */

export function PlayerControls({
	isPaused,
	variant = "desktop",
	onPlayPause,
}: PlayerControlsProps) {
	const isAtFirstChapter = playerSelectors.isAtFirstChapter(
		usePlayerStore.getState(),
	);
	const isAtLastChapter = playerSelectors.isAtLastChapter(
		usePlayerStore.getState(),
	);

	// Mobile variant - large touch-friendly buttons
	if (variant === "mobile") {
		return (
			<div className="flex items-center justify-center gap-3 md:gap-4">
				{/* Previous chapter */}
				<Button
					variant="ghost"
					size="icon"
					onClick={getPlayerActions().goToPreviousChapter}
					disabled={isAtFirstChapter}
					className="rounded-full hover:bg-white/10 disabled:opacity-30"
				>
					<SkipBack className="h-7 w-7" />
				</Button>

				{/* Rewind 30 seconds */}
				<Button
					variant="ghost"
					size="iconLg"
					onClick={() =>
						getPlayerActions().seekRelative(-SKIP_INTERVAL_SECONDS)
					}
					className="rounded-full hover:bg-white/10 [&_svg]:size-8 [&_svg]:stroke-[1.2]"
				>
					<div className="relative">
						<RotateCcw className="h-7 w-7" />
						<span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">
							{SKIP_INTERVAL_SECONDS}
						</span>
					</div>
				</Button>

				{/* Play/Pause - Large center button */}
				<Button
					variant="ghost"
					size="iconLg"
					onClick={onPlayPause}
					className="h-16 w-16 rounded-full bg-white hover:bg-gray-100 text-black shadow-lg hover:scale-105 transition-transform"
				>
					{isPaused ? (
						<Play className="h-10 w-10" />
					) : (
						<Pause className="h-10 w-10" />
					)}
				</Button>

				{/* Forward 30 seconds */}
				<Button
					variant="ghost"
					size="iconLg"
					onClick={() => getPlayerActions().seekRelative(SKIP_INTERVAL_SECONDS)}
					className="rounded-full hover:bg-white/10 [&_svg]:size-8 [&_svg]:stroke-[1.2]"
				>
					<div className="relative">
						<RotateCw />
						<span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">
							{SKIP_INTERVAL_SECONDS}
						</span>
					</div>
				</Button>

				{/* Next chapter */}
				<Button
					variant="ghost"
					size="icon"
					onClick={getPlayerActions().goToNextChapter}
					disabled={isAtLastChapter}
					className="rounded-full hover:bg-white/10 disabled:opacity-30"
				>
					<SkipForward className="h-7 w-7" />
				</Button>
			</div>
		);
	}

	// Desktop variant - compact buttons
	return (
		<div className="flex items-center gap-4">
			{/* Previous chapter */}
			<Button
				variant="ghost"
				size="icon"
				className="h-10 w-10 text-gray-400 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
				onClick={getPlayerActions().goToPreviousChapter}
				disabled={isAtFirstChapter}
				aria-label="Previous chapter"
			>
				<SkipBack className="h-7 w-7" />
			</Button>

			{/* Rewind 30 seconds */}
			<Button
				variant="ghost"
				size="iconLg"
				className="text-gray-400 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full relative [&_svg]:size-8 [&_svg]:stroke-[1.2]"
				onClick={() => getPlayerActions().seekRelative(-SKIP_INTERVAL_SECONDS)}
				title={`Rewind ${SKIP_INTERVAL_SECONDS} seconds`}
				aria-label={`Rewind ${SKIP_INTERVAL_SECONDS} seconds`}
			>
				<div className="relative">
					<RotateCcw />
					<span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[7.5px] font-bold">
						{SKIP_INTERVAL_SECONDS}
					</span>
				</div>
			</Button>

			{/* Play/Pause button */}
			<PlayButton className="cursor-pointer group relative h-14 w-14 rounded-full flex items-center justify-center bg-gradient-to-br from-white/10 via-white/5 to-primary/20 backdrop-blur-2xl border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.3),inset_0_0_0_3px_rgba(0,0,0,0.1),0_8px_20px_rgba(0,0,0,0.2)] hover:scale-105 active:scale-95 transition-all duration-500 text-white">
				<div className="absolute inset-0 rounded-full bg-[conic-gradient(from_180deg_at_50%_50%,rgba(255,255,255,0)_0deg,rgba(255,255,255,0.1)_60deg,rgba(255,255,255,0)_120deg,rgba(255,255,255,0)_180deg,rgba(255,255,255,0.1)_240deg,rgba(255,255,255,0)_300deg)] opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
				<span className="relative z-10 drop-shadow-md">
					{isPaused ? (
						<PlayIcon className="h-7 w-7 fill-current" />
					) : (
						<PauseIcon className="h-7 w-7 fill-current" />
					)}
				</span>
			</PlayButton>

			{/* Forward 30 seconds */}
			<Button
				variant="ghost"
				size="iconLg"
				className="text-gray-400 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full relative [&_svg]:size-8 [&_svg]:stroke-[1.2]"
				onClick={() => getPlayerActions().seekRelative(SKIP_INTERVAL_SECONDS)}
				title={`Fast forward ${SKIP_INTERVAL_SECONDS} seconds`}
				aria-label={`Fast forward ${SKIP_INTERVAL_SECONDS} seconds`}
			>
				<div className="relative">
					<RotateCw />
					<span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[7.5px] font-bold">
						{SKIP_INTERVAL_SECONDS}
					</span>
				</div>
			</Button>

			{/* Next chapter */}
			<Button
				variant="ghost"
				size="icon"
				className="h-10 w-10 text-gray-400 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
				onClick={getPlayerActions().goToNextChapter}
				disabled={isAtLastChapter}
				aria-label="Next chapter"
			>
				<SkipForward className="h-7 w-7" />
			</Button>
		</div>
	);
}
